/**
 * iter251 — Admin Activations page.
 *
 * The iter250 hard gate created a population nobody could see: accounts that
 * exist but are inert. This page is the operational answer — where people
 * fall out of the activation email, who is still pending, and how long each
 * one has before the day-14 cleanup removes it.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  UserCheck, Loader2, RefreshCcw, Send, Trash2, PlayCircle, Clock,
  Eye, MousePointerClick, MailX, Search, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import ActivationFunnelCard from '@/components/admin/ActivationFunnelCard';

const STAGE_META = {
  never_opened: { label: 'Never opened', icon: MailX, chip: 'bg-slate-800 border-slate-700 text-slate-300' },
  opened_not_clicked: { label: 'Opened, no click', icon: Eye, chip: 'bg-sky-950/40 border-sky-800 text-sky-200' },
  clicked_not_activated: { label: 'Clicked, not activated', icon: MousePointerClick, chip: 'bg-violet-950/40 border-violet-800 text-violet-200' },
};

const daysLeftOf = (purgeAt) => {
  if (!purgeAt) return null;
  const ms = new Date(purgeAt).getTime() - Date.now();
  return Math.ceil(ms / 86400000);
};

const ActivationsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [running, setRunning] = useState(false);
  const [confirmUser, setConfirmUser] = useState(null);
  const [q, setQ] = useState('');

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const r = await api.get('/admin/activations');
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load activations', { id: 'act-load-fail' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const nudge = async (user) => {
    setBusyId(user.id);
    try {
      const r = await api.post(`/admin/activations/${user.id}/nudge`);
      toast.success(
        r.data?.already_verified
          ? 'That account is already activated.'
          : `Activation email resent to ${user.email}`,
        { id: 'act-nudge' },
      );
      await load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resend', { id: 'act-nudge-fail' });
    } finally {
      setBusyId(null);
    }
  };

  const purge = async (user) => {
    setBusyId(user.id);
    try {
      await api.delete(`/admin/activations/${user.id}`);
      toast.success(`Deleted unactivated account ${user.email}`, { id: 'act-purge' });
      setConfirmUser(null);
      await load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete', { id: 'act-purge-fail' });
    } finally {
      setBusyId(null);
    }
  };

  const runMaintenance = async () => {
    setRunning(true);
    try {
      const r = await api.post('/admin/activations/run-maintenance');
      const sent = r.data?.reminders_sent ?? 0;
      const purged = r.data?.purged ?? 0;
      toast.success(
        `Maintenance done — ${sent} reminder${sent === 1 ? '' : 's'} sent, ${purged} account${purged === 1 ? '' : 's'} removed.`,
        { id: 'act-maintenance' },
      );
      await load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Maintenance failed', { id: 'act-maint-fail' });
    } finally {
      setRunning(false);
    }
  };

  const pending = data?.pending || [];
  const stageByUser = useMemo(() => {
    const map = {};
    (data?.funnel?.stalled || []).forEach((s) => { map[s.user_id] = s; });
    return map;
  }, [data]);

  const filtered = useMemo(() => {
    if (!q.trim()) return pending;
    const term = q.trim().toLowerCase();
    return pending.filter(
      (p) => (p.email || '').toLowerCase().includes(term)
        || (p.full_name || '').toLowerCase().includes(term),
    );
  }, [pending, q]);

  const reminderDays = data?.policy?.reminder_after_days ?? 7;
  const purgeDays = data?.policy?.purge_after_days ?? 14;
  const dueSoon = pending.filter((p) => {
    const d = daysLeftOf(p.purge_at);
    return d !== null && d <= 3;
  }).length;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-activations-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-sky-400" />
            Activations
          </h1>
          <p className="text-slate-400 text-sm mt-0.5 max-w-2xl">
            Accounts created under the activation gate that have not confirmed their
            email. A reminder goes out on day {reminderDays}; anything still
            unconfirmed on day {purgeDays} is deleted. Verified and legacy accounts
            never appear here and cannot be removed from this page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-[#1F2A4D] text-slate-200 hover:bg-[#0A1024]"
            onClick={runMaintenance}
            disabled={running}
            data-testid="activations-run-maintenance"
          >
            {running
              ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Running…</>
              : <><PlayCircle className="h-3.5 w-3.5 mr-1.5" /> Run cleanup now</>}
          </Button>
          <Button
            variant="outline"
            className="border-[#1F2A4D] text-slate-200 hover:bg-[#0A1024]"
            onClick={() => load()}
            data-testid="activations-refresh"
          >
            <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-sky-400" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
            <div className="lg:col-span-2">
              <ActivationFunnelCard funnel={data?.funnel} />
            </div>
            <div className="space-y-4">
              <Card data-testid="activations-pending-summary">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-slate-100">Pending right now</CardTitle>
                </CardHeader>
                <CardContent>
                  <p
                    className="text-4xl font-bold text-amber-400 tabular-nums"
                    data-testid="activations-pending-count"
                  >
                    {data?.pending_count || 0}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    inert accounts waiting on one click
                  </p>
                </CardContent>
              </Card>
              <Card
                className={dueSoon > 0 ? 'border-rose-900/60' : undefined}
                data-testid="activations-duesoon-card"
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-slate-100 flex items-center gap-2">
                    {dueSoon > 0 && <AlertTriangle className="h-4 w-4 text-rose-400" />}
                    Deleting within 3 days
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p
                    className={`text-4xl font-bold tabular-nums ${dueSoon > 0 ? 'text-rose-400' : 'text-slate-500'}`}
                    data-testid="activations-duesoon-count"
                  >
                    {dueSoon}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    last chance to nudge them
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          <Card>
            <CardHeader className="pb-3 flex-row items-center justify-between gap-3 flex-wrap space-y-0">
              <div>
                <CardTitle className="text-base text-slate-100">Pending accounts</CardTitle>
                <CardDescription className="text-slate-400 text-sm">
                  Oldest first — the ones nearest deletion are at the top.
                </CardDescription>
              </div>
              <div className="relative w-full sm:w-64">
                <Search className="h-4 w-4 text-slate-500 absolute left-3 top-2.5" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search email or name"
                  className="pl-9 bg-[#0A1024] border-[#1F2A4D] text-slate-100 placeholder:text-slate-500"
                  data-testid="activations-search"
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {filtered.length === 0 ? (
                <div className="py-16 text-center text-slate-400" data-testid="activations-empty">
                  <UserCheck className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
                  <p className="text-sm">
                    {pending.length === 0
                      ? 'Nobody is stuck — every gated account has activated.'
                      : 'No pending account matches that search.'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-[#1F2A4D]" data-testid="activations-list">
                  {filtered.map((p) => {
                    const stalled = stageByUser[p.id];
                    const stage = STAGE_META[stalled?.stage] || STAGE_META.never_opened;
                    const StageIcon = stage.icon;
                    const left = daysLeftOf(p.purge_at);
                    const urgent = left !== null && left <= 3;
                    return (
                      <div
                        key={p.id}
                        className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap hover:bg-[#0A1024]/60 transition-colors"
                        data-testid={`activations-row-${p.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-sm font-medium text-slate-100 truncate">
                              {p.email}
                            </span>
                            <Badge className={`${stage.chip} border text-[10px]`}>
                              <StageIcon className="h-3 w-3 mr-1" />
                              {stage.label}
                            </Badge>
                            {p.reminder_sent_at && (
                              <Badge className="bg-amber-950/40 border-amber-800 text-amber-200 border text-[10px]">
                                Reminded
                              </Badge>
                            )}
                            {p.pending_email_claim && (
                              <Badge className="bg-slate-800 border-slate-700 text-slate-400 border text-[10px]">
                                No email yet
                              </Badge>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                            {p.full_name && <span>{p.full_name}</span>}
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              waiting {p.days_waiting}d
                            </span>
                            <span className={urgent ? 'text-rose-300 font-medium' : ''}>
                              · deletes in {left === null ? '—' : `${Math.max(0, left)}d`}
                            </span>
                            {p.signup_ip && <span>· {p.signup_ip}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#1F2A4D] text-slate-200 hover:bg-[#0A1024]"
                            onClick={() => nudge(p)}
                            disabled={busyId === p.id || p.pending_email_claim}
                            data-testid={`activations-nudge-${p.id}`}
                          >
                            {busyId === p.id
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <><Send className="h-3.5 w-3.5 mr-1.5" /> Nudge</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-rose-900/70 text-rose-300 hover:bg-rose-950/40 hover:text-rose-200"
                            onClick={() => setConfirmUser(p)}
                            disabled={busyId === p.id}
                            data-testid={`activations-delete-${p.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <AlertDialog open={!!confirmUser} onOpenChange={(o) => !o && setConfirmUser(null)}>
        <AlertDialogContent data-testid="activations-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this unactivated account?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong className="text-slate-200">{confirmUser?.email}</strong> and its
              sessions will be removed immediately. The deletion itself is written to the
              audit log, so the record of it survives. This cannot be undone — they would
              have to sign up again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="activations-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={(e) => { e.preventDefault(); purge(confirmUser); }}
              data-testid="activations-delete-confirm-btn"
            >
              Delete account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ActivationsPage;
